import { ScbMyBillingPendingInvoiceDetail } from '../../scb.models';
import { extractMyBillingInvoicePrintPayload } from '../../scb-ui.utils';
import { ScbPremiumInvoiceLineVm, ScbPremiumInvoiceVm } from './scb-premium-invoice.models';

export type ScbPremiumInvoicePeriodContext = {
  periodLabel?: string | null;
  periodStart?: string | null;
  periodEnd?: string | null;
  invoiceDate?: string | null;
  dueDate?: string | null;
  invoiceNumber?: string | null;
  amount?: number | null;
  remainingAmount?: number | null;
};

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}

function str(value: unknown): string {
  return String(value ?? '').trim();
}

function num(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function parseDate(value: unknown): Date | null {
  if (value == null || value === '') return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }
  const d = new Date(String(value));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Premium invoice dates match the mock: `dd-MM-yyyy`. */
export function formatScbPremiumDate(value: unknown): string {
  const d = parseDate(value);
  if (!d) return '—';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yyyy = String(d.getFullYear());
  return `${dd}-${mm}-${yyyy}`;
}

export function formatScbPremiumDateTime(value: unknown = new Date()): string {
  const d = parseDate(value) ?? new Date();
  const date = formatScbPremiumDate(d);
  let hours = d.getHours();
  const minutes = String(d.getMinutes()).padStart(2, '0');
  const ampm = hours >= 12 ? 'PM' : 'AM';
  hours = hours % 12 || 12;
  return `${date} ${hours}:${minutes} ${ampm}`;
}

/** Whole amounts like the mock (`5,000`); keep decimals when present. */
export function formatScbPremiumMoney(value: unknown): string {
  const n = num(value);
  if (n == null) return '0';
  const rounded = Math.round(n * 100) / 100;
  if (Number.isInteger(rounded)) {
    return new Intl.NumberFormat('en-PK', { maximumFractionDigits: 0 }).format(rounded);
  }
  return new Intl.NumberFormat('en-PK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(rounded);
}

const MONTH_SHORT = [
  'Jan',
  'Feb',
  'Mar',
  'Apr',
  'May',
  'Jun',
  'Jul',
  'Aug',
  'Sep',
  'Oct',
  'Nov',
  'Dec',
] as const;

export function formatScbPremiumPeriodLabel(
  periodLabel: unknown,
  periodStart: unknown,
  periodEnd: unknown
): string {
  const named = str(periodLabel);
  if (named) return named;

  const start = parseDate(periodStart);
  if (start) {
    return `${MONTH_SHORT[start.getMonth()]}-${start.getFullYear()}`;
  }

  const end = parseDate(periodEnd);
  if (end) {
    return `${MONTH_SHORT[end.getMonth()]}-${end.getFullYear()}`;
  }

  return '—';
}

export function formatScbPremiumBillingPeriod(
  periodStart: unknown,
  periodEnd: unknown,
  periodLabel: unknown
): string {
  const start = formatScbPremiumDate(periodStart);
  const end = formatScbPremiumDate(periodEnd);
  if (start !== '—' && end !== '—') {
    return `${start} to ${end}`;
  }
  const named = str(periodLabel);
  return named || (start !== '—' ? start : end !== '—' ? end : '—');
}

function lineAmount(line: Record<string, unknown>): number {
  return (
    num(line['lineTotal']) ??
    num(line['netAmount']) ??
    num(line['amount']) ??
    num(line['totalAmount']) ??
    (num(line['quantity']) ?? 1) * (num(line['unitPrice']) ?? num(line['rate']) ?? 0)
  );
}

function mapInvoiceLines(
  invoice: Record<string, unknown>,
  ctx: ScbPremiumInvoicePeriodContext
): ScbPremiumInvoiceLineVm[] {
  const period = formatScbPremiumPeriodLabel(ctx.periodLabel, ctx.periodStart, ctx.periodEnd);
  const billingDate = formatScbPremiumDate(
    invoice['invoiceDate'] ?? invoice['createdAt'] ?? ctx.invoiceDate ?? ctx.periodStart
  );
  const dueDate = formatScbPremiumDate(invoice['dueDate'] ?? ctx.dueDate ?? ctx.periodEnd);

  const rawLines = Array.isArray(invoice['lines']) ? invoice['lines'] : [];
  const mapped: ScbPremiumInvoiceLineVm[] = [];

  for (const raw of rawLines) {
    const line = asRecord(raw);
    if (!line) continue;
    const title =
      str(line['productName']) ||
      str(line['itemName']) ||
      str(line['name']) ||
      str(line['description']) ||
      'BizCare ERP';
    const subtitleRaw =
      str(line['description']) && str(line['description']) !== title
        ? str(line['description'])
        : str(line['remarks']) || str(line['lineDescription']) || null;
    const amount = lineAmount(line);
    mapped.push({
      period,
      serviceTitle: title,
      serviceSubtitle: subtitleRaw,
      billingDate,
      dueDate,
      price: formatScbPremiumMoney(amount),
      net: formatScbPremiumMoney(amount),
    });
  }

  if (mapped.length) return mapped;

  const fallbackAmount =
    num(ctx.remainingAmount) ??
    num(ctx.amount) ??
    num(invoice['netAmount']) ??
    num(invoice['grandTotal']) ??
    num(invoice['totalAmount']) ??
    0;

  return [
    {
      period,
      serviceTitle: 'BizCare ERP',
      serviceSubtitle: 'Subscription',
      billingDate,
      dueDate,
      price: formatScbPremiumMoney(fallbackAmount),
      net: formatScbPremiumMoney(fallbackAmount),
    },
  ];
}

function resolveBilledToAddress(
  invoice: Record<string, unknown>,
  customer: Record<string, unknown> | null
): string | null {
  const parts = [
    customer?.['address'] ?? customer?.['addressLine1'] ?? invoice['customerAddress'],
    customer?.['addressLine2'],
    customer?.['city'] ?? invoice['customerCity'],
    customer?.['state'] ?? customer?.['province'],
  ]
    .map((x) => str(x))
    .filter(Boolean);
  return parts.length ? parts.join(', ') : null;
}

/** Build BizCare premium invoice VM from a sales-invoice-shaped payload + optional SCB period fields. */
export function mapScbPremiumInvoiceVmFromInvoice(
  invoiceRaw: unknown,
  ctx: ScbPremiumInvoicePeriodContext = {}
): ScbPremiumInvoiceVm {
  const invoice = asRecord(invoiceRaw) ?? {};
  const customer = asRecord(invoice['customer']) ?? asRecord(invoice['businessPartner']);

  const billedToName =
    str(invoice['customerName']) ||
    str(customer?.['businessPartnerName']) ||
    str(customer?.['name']) ||
    str(customer?.['bpName']) ||
    '—';

  const invoiceNumber =
    str(invoice['invoiceNumber']) ||
    str(invoice['billNo']) ||
    str(invoice['salesInvoiceNumber']) ||
    str(ctx.invoiceNumber) ||
    '—';

  const invoiceDate = formatScbPremiumDate(
    invoice['invoiceDate'] ?? invoice['createdAt'] ?? ctx.invoiceDate
  );
  const dueDate = formatScbPremiumDate(invoice['dueDate'] ?? ctx.dueDate);
  const billingPeriod = formatScbPremiumBillingPeriod(
    ctx.periodStart ?? invoice['periodStart'],
    ctx.periodEnd ?? invoice['periodEnd'],
    ctx.periodLabel ?? invoice['periodLabel']
  );

  const lines = mapInvoiceLines(invoice, ctx);
  const totalPayable = formatScbPremiumMoney(
    num(ctx.remainingAmount) ??
      num(ctx.amount) ??
      num(invoice['netAmount']) ??
      num(invoice['grandTotal']) ??
      num(invoice['totalAmount']) ??
      lines.reduce((sum, line) => sum + (num(line.net.replace(/,/g, '')) ?? 0), 0)
  );

  return {
    billedToName,
    billedToAddress: resolveBilledToAddress(invoice, customer),
    invoiceNumber,
    invoiceDate,
    billingPeriod,
    dueDate,
    lines,
    totalPayable,
    generatedOn: formatScbPremiumDateTime(new Date()),
  };
}

/** Build the tenant-facing BizCare premium invoice view-model from my-billing detail. */
export function mapScbPremiumInvoiceVm(
  detail: ScbMyBillingPendingInvoiceDetail
): ScbPremiumInvoiceVm {
  const invoiceRaw = extractMyBillingInvoicePrintPayload(detail) ?? {};
  return mapScbPremiumInvoiceVmFromInvoice(invoiceRaw, {
    periodLabel: detail.periodLabel,
    periodStart: detail.periodStart,
    periodEnd: detail.periodEnd,
    invoiceDate: detail.invoiceDate,
    dueDate: detail.dueDate,
    invoiceNumber: detail.invoiceNumber,
    amount: detail.amount,
    remainingAmount: detail.remainingAmount,
  });
}
